/**
 * The reservation link an owner sends on WhatsApp: the customer opens it in
 * the Dale app, the exact service and time are already chosen, and they only
 * confirm with their number. Pure helpers; the page wires them up.
 *
 * The query names are the same ones the AI assistant links use
 * (apps/demo/lib/booking-link.ts BOOKING_LINK_PARAMS); renaming one breaks
 * links already sent.
 */

import type { CountryCode } from './country';

const PRODUCTION_APP = 'https://app.dalepanama.com';
const PRODUCTION_APP_VENEZUELA = 'https://app.dalevenezuela.com';
const STAGING_APP = 'https://appstaging.dalepanama.com';

/** Each CRM host and the customer app of its own environment. */
const APP_FOR_HOST: Readonly<Record<string, string>> = {
  'crm.dalepanama.com': PRODUCTION_APP,
  // The production CRM Worker answers on its own address too, with production data behind it.
  'dale-business.martinfurbano.workers.dev': PRODUCTION_APP,
  'demo.dalepanama.com': 'https://demo.dalepanama.com',
  'demo.dalevenezuela.com': 'https://demo.dalevenezuela.com',
};

/**
 * Anything not named (staging, a local dev server) links to staging, never to
 * production. Production has one customer app per market, so a Venezuelan
 * business's customers land on app.dalevenezuela.com; every other environment
 * has a single customer app.
 */
export function consumerOrigin(hostname: string, country: CountryCode = 'PA'): string {
  const origin = APP_FOR_HOST[hostname] ?? STAGING_APP;
  return origin === PRODUCTION_APP && country === 'VE' ? PRODUCTION_APP_VENEZUELA : origin;
}

export type LinkSlot = { serviceId: string; startsAt: string; staffId?: string };

export function bookingLinkUrl(origin: string, businessId: string, slot?: LinkSlot): string {
  const page = `${origin}/comercio/${encodeURIComponent(businessId)}`;
  if (!slot) return page;
  // Colons are legal in a query, so the time stays readable when the link is pasted in a chat.
  const param = (name: string, value: string) => `${name}=${encodeURIComponent(value).replace(/%3A/gi, ':')}`;
  const params = [param('servicio', slot.serviceId), param('hora', slot.startsAt)];
  if (slot.staffId) params.push(param('equipo', slot.staffId));
  return `${page}?${params.join('&')}`;
}

type MessageInput = {
  customerName?: string;
  businessName: string;
  serviceName?: string;
  startsAt?: string;
  timeZone: string;
  url: string;
  now?: Date;
};

/** What the owner sends: short, warm, and with the day and time spelled out. */
export function linkMessage({ customerName, businessName, serviceName, startsAt, timeZone, url, now = new Date() }: MessageInput): string {
  const first = customerName?.trim().split(/\s+/)[0];
  const greeting = first ? `Hola, ${first}.` : 'Hola.';
  if (!serviceName || !startsAt) return `${greeting} Reserva en ${businessName} aquí: ${url}`;
  const when = `${dayPhrase(startsAt, timeZone, now)}, a las ${slotTimeLabel(startsAt, timeZone)}`;
  // The time already ends in "a. m.", so no second period after it.
  return `${greeting} Te aparté ${serviceName} en ${businessName} para ${when}${when.endsWith('.') ? '' : '.'} Confírmalo aquí: ${url}`;
}

/** wa.me with the text ready; without a number WhatsApp asks who to send it to. */
export function whatsAppShareUrl(phone: string, text: string): string {
  const digits = phone.replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export type DayChoice = { date: string; label: string };

/** Today and the days after it, as the business calendar reads them. */
export function dayChoices(now: Date, timeZone: string, count: number): DayChoice[] {
  const today = dateKey(now, timeZone);
  return Array.from({ length: count }, (_unused, offset) => {
    const date = addDays(today, offset);
    return { date, label: offset === 0 ? 'Hoy' : offset === 1 ? 'Mañana' : shortDay(date) };
  });
}

export function slotTimeLabel(startsAt: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es-PA', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(startsAt));
}

/** "mañana, martes 6 de octubre" or "jueves 8 de octubre". */
function dayPhrase(startsAt: string, timeZone: string, now: Date): string {
  const date = new Date(startsAt);
  const dated = new Intl.DateTimeFormat('es-PA', { timeZone, weekday: 'long', day: 'numeric', month: 'long' })
    .format(date)
    .replace(',', '');
  const key = dateKey(date, timeZone);
  const today = dateKey(now, timeZone);
  if (key === today) return `hoy, ${dated}`;
  if (key === addDays(today, 1)) return `mañana, ${dated}`;
  return dated;
}

function dateKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

/** Calendar arithmetic on a YYYY-MM-DD key, at noon UTC so no offset can tip the day. */
function addDays(key: string, days: number): string {
  const date = new Date(`${key}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const WEEKDAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

function shortDay(key: string): string {
  const date = new Date(`${key}T12:00:00.000Z`);
  return `${WEEKDAY_SHORT[date.getUTCDay()]} ${date.getUTCDate()}`;
}

/** Who the customer is booked with: anyone free at that time, or one person by id. */
export type StaffChoice = 'any' | string;

/**
 * The times to offer for one choice of person. With anyone, each time shows
 * once (the first person free then), and the link leaves the person open so
 * the customer still gets the time if that one fills.
 */
export function timesFor<T extends { startsAt: string; staffId?: string }>(slots: readonly T[], staff: StaffChoice): T[] {
  if (staff !== 'any') return slots.filter((slot) => slot.staffId === staff);
  const seen = new Set<number>();
  return slots
    .filter((slot) => {
      const instant = Date.parse(slot.startsAt);
      if (seen.has(instant)) return false;
      seen.add(instant);
      return true;
    })
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}
